import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher } from '@remix-run/react'
import { CartContext } from 'app/routes/_layout'
import * as React from 'react'
import { useHydrated } from 'remix-utils/use-hydrated'

import { type GetProduct } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-product'
import { type GetProductVariant } from '~/models/ecommerce-providers/sylius/shop/fetch/product-variant/get-product-variant'
import {
	PRODUCTS_SLUG_FORM_ADD_TO_CART,
	PRODUCTS_SLUG_INPUTS,
	productsSlugAddToCartFormSchema,
} from '~/routes/($lang).products.$slug/slug.component'
import { ROOT_INPUTS } from '~/routes/_layout/root.component.tsx'
import type {
  ProductOptionValueNormalized
} from '~/models/ecommerce-providers/sylius/shop/interface/product-option-value.ts'

type UseOptimisticAddToCartProps = {
	product: GetProduct
	variant: GetProductVariant
}

function useOptimisticAddToCart({
	product,
	variant,
}: UseOptimisticAddToCartProps) {
	const { setContext } = React.useContext(CartContext)!
	const isHydrated = useHydrated()
	const fetcher = useFetcher({
		key: PRODUCTS_SLUG_FORM_ADD_TO_CART.generateFetcherKey!({
			id: product.slug!,
		}),
	})
  const formValues = {
    [ROOT_INPUTS.subaction]: PRODUCTS_SLUG_FORM_ADD_TO_CART.subaction,
    [ROOT_INPUTS.noJs]: String(!isHydrated),
  }
	const [form, fields] = useForm({
		defaultValue: {
			...formValues,
      [PRODUCTS_SLUG_INPUTS.variantId]: variant['@id']!,
      [PRODUCTS_SLUG_INPUTS.quantity]: 1,
      [PRODUCTS_SLUG_INPUTS.slug]: product.slug,
      [PRODUCTS_SLUG_INPUTS.variantImage]: variant.images?.[0].path || product.images![0].path,
      [PRODUCTS_SLUG_INPUTS.variantPrice]: variant.price,
      [PRODUCTS_SLUG_INPUTS.name]: product.name,
      [PRODUCTS_SLUG_INPUTS.variantOptionsValuesLength]: (variant.optionValues as ProductOptionValueNormalized[])?.length,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: productsSlugAddToCartFormSchema,
			})
		},
		shouldValidate: 'onSubmit',
	})

  React.useEffect(() => {
    form.update({
      name: PRODUCTS_SLUG_INPUTS.variantId,
      value: variant['@id']!,
    })
  }, [variant['@id']])

	React.useEffect(() => {
		const optimistic = fetcher.formData
			? Object.fromEntries(fetcher.formData.entries())
			: fetcher.data

		if (optimistic) {
			setContext((prev) => ({
				...prev,
				[variant.id]: {
					optimisticQuantity:
						Number(optimistic[PRODUCTS_SLUG_INPUTS.quantity]) || 1,
					optimisticDeleting: false,
					optimisticPrice:
						Number(optimistic[PRODUCTS_SLUG_INPUTS.quantity]) * variant.price,
					optimisticItem: {
						id: Number(optimistic[PRODUCTS_SLUG_INPUTS.variantId]),
						optimistic: true,
						variant: {
							lowestPriceBeforeDiscount: null,
							'@id': optimistic[PRODUCTS_SLUG_INPUTS.variantId] as string,
							images: [
								{
									path: optimistic[PRODUCTS_SLUG_INPUTS.variantImage] as string,
								},
							],
							optionValues: [
								...Array(
									optimistic[PRODUCTS_SLUG_INPUTS.variantOptionsValuesLength],
								).keys(),
							].map((i) => ({
								code: optimistic[`variantOptionsValues[${i}][code]`] as string,
								value: optimistic[
									`variantOptionsValues[${i}][value]`
								] as string,
								option: {
									name: optimistic[
										`variantOptionsValues[${i}][name]`
									] as string,
								},
							})),
							product: {
								slug: optimistic[PRODUCTS_SLUG_INPUTS.slug] as string,
							},
						},
						quantity: 1,
						total: Number(optimistic[PRODUCTS_SLUG_INPUTS.variantPrice]),
						unitPrice: Number(optimistic[PRODUCTS_SLUG_INPUTS.variantPrice]),
						productName: optimistic[PRODUCTS_SLUG_INPUTS.name] as string,
					},
				},
			}))
		}
	}, [fetcher, setContext, variant])

	return {
		fetcher,
    formValues,
		form,
		fields,
		disabled:
			!product.enabled ||
			!variant ||
			!variant.inStock ||
			fetcher.state !== 'idle',
	}
}

export { useOptimisticAddToCart }
